// Three-way merge for a note's title and body, shared by the web app and the phone app
// (StudyBoard-Android/utils/textMerge.ts is a copy — keep them identical).
//
// When two devices edit the same note from the same starting text ("base"), each change is
// turned into a patch against base and applied to the other side. The merge only counts as
// clean when every patch applies exactly AND applying in either order gives the same text.
// Anything less is reported as a conflict, so the caller can keep both versions instead of
// silently guessing.
import DiffMatchPatch from 'diff-match-patch';

export interface NoteText { title: string; content: string }
export type MergeResult =
  | { clean: true; merged: NoteText }
  | { clean: false; merged: null };

const dmp = new DiffMatchPatch();
// A patch's surrounding text may have moved (the other side added text above it), but it must
// still be found almost exactly: at most 10% of the context may differ.
dmp.Match_Threshold = 0.1;
dmp.Match_Distance = 100000;
dmp.Patch_DeleteThreshold = 0;    // a deletion only applies when the deleted text is unchanged
dmp.Patch_Margin = 8;

function applyAll(base: string, change: string, onto: string): string | null {
  const patches = dmp.patch_make(base, change);
  const [out, ok] = dmp.patch_apply(patches, onto);
  return ok.every(Boolean) ? out : null;
}

/** Merges one text field. Returns null when the two edits touch the same place. */
export function merge3(base: string, ours: string, theirs: string): string | null {
  if (ours === theirs) return ours;
  if (ours === base) return theirs;
  if (theirs === base) return ours;
  const a = applyAll(base, ours, theirs);   // our edit replayed on top of theirs
  const b = applyAll(base, theirs, ours);   // their edit replayed on top of ours
  return a !== null && a === b ? a : null;
}

export function mergeNoteText(base: NoteText, ours: NoteText, theirs: NoteText): MergeResult {
  const title = merge3(base.title, ours.title, theirs.title);
  const content = merge3(base.content, ours.content, theirs.content);
  if (title === null || content === null) return { clean: false, merged: null };
  return { clean: true, merged: { title, content } };
}

/** A version of a note's text that lost a conflict, kept on the note so nothing is lost. */
export interface TextConflictCopy {
  title: string;
  content: string;
  from: 'web' | 'mobile' | 'unknown';
  /** When this copy was set aside. */
  savedAt: number;
  /** The revision this text had when it was set aside. */
  rev: number;
}
export const MAX_CONFLICT_COPIES = 5;
